//Frontend/src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";
import "./unicodex_ui/styles/globals.css";

import LandingPage from "./unicodex_ui/pages/LandingPage";
import VerifyPage from "./unicodex_ui/pages/VerifyPage";
import RegisterPage from "./unicodex_ui/pages/RegisterPage";
import PrefixApplyPage from "./unicodex_ui/pages/PrefixApplyPage";
import DocsPage from "./unicodex_ui/pages/DocsPage";
import ManufacturerLoginPage from "./unicodex_ui/pages/ManufacturerLoginPage";
import ManufacturerDashboardPage from "./unicodex_ui/pages/ManufacturerDashboardPage";
import ManufacturerProductsPage from "./unicodex_ui/pages/ManufacturerProductsPage";
import ManufacturerBatchesPage from "./unicodex_ui/pages/ManufacturerBatchesPage";
import ManufacturerCodesPage from "./unicodex_ui/pages/ManufacturerCodesPage";
import PurchaserDashboardPage from "./unicodex_ui/pages/PurchaserDashboardPage";
import InventoryDashboardPage from "./unicodex_ui/pages/InventoryDashboardPage";
import OpsDashboardPage from "./unicodex_ui/pages/OpsDashboardPage";
import PosTerminalPage from "./unicodex_ui/pages/PosTerminalPage";
import PosInventory from "./unicodex_ui/pages/PosInventory";
import PortalAccountingPage from "./unicodex_ui/pages/PortalAccountingPage";
import PortalShipmentsPage from "./unicodex_ui/pages/PortalShipmentsPage";
import PortalShipmentDetailPage from "./unicodex_ui/pages/PortalShipmentDetailPage";
import PortalMeteredPage from "./unicodex_ui/pages/PortalMeteredPage";
import PortalForecastPage from "./unicodex_ui/pages/PortalForecastPage";
import PortalComplianceDashboardPage from "./unicodex_ui/pages/PortalComplianceDashboardPage";
import { getStoredPortalUser, getStoredPermissions } from "./unicodex_ui/services/authApi";

const ACCOUNTING_PERMS = [
  "accounting.accounts.read",
  "accounting.mappings.read",
  "accounting.journals.read",
  "accounting.reports.trial_balance.read",
  "accounting.reports.pnl.read",
  "accounting.reports.tax_summary.read",
];

const LOGISTICS_PERMS = [
  "logistics.shipments.read",
  "logistics.shipments.create",
  "logistics.shipments.update",
];

const METERED_PERMS = [
  "meter.read",
  "meter.session.open",
  "meter.session.record",
  "meter.session.close",
  "meter.session.read",
];

const ANALYTICS_PERMS = [
  "analytics.forecast.run",
  "analytics.forecast.read",
];

const COMPLIANCE_PERMS = [
  "compliance.read",
  "compliance.verifications.read",
  "compliance.risk.read",
  "compliance.shipments.read",
];

// eslint-disable-next-line
function PortalNoAccessPage() {
  return (
    <div className="ucx-page">
      <div className="ucx-card">
        <h2 style={{ marginTop: 0 }}>No portal access</h2>
        <p className="ucx-muted">
          Your account is authenticated, but has no portal modules assigned. Ask an admin to grant role permissions.
        </p>
      </div>
    </div>
  );
}

function RequirePortalAccess({ allowedRoles, anyPermissions, children }) {
  const token = localStorage.getItem("ucx_mfg_token");
  const user = getStoredPortalUser();
  const perms = getStoredPermissions();
  if (!token) {
    return <Navigate to="/portal/login" replace />;
  }
  if (!user || !user.role) {
    return <Navigate to="/portal/login" replace />;
  }
  if (Array.isArray(allowedRoles) && allowedRoles.length && !allowedRoles.includes(user.role)) {
    return <Navigate to="/portal/login" replace />;
  }
  if (Array.isArray(anyPermissions) && anyPermissions.length) {
    const ok = anyPermissions.some((p) => perms.includes(p));
    if (!ok) return <Navigate to="/portal/login" replace />;
  }

  return children;
}

function PortalHomeRedirect() {
  const token = localStorage.getItem("ucx_mfg_token");
  const user = getStoredPortalUser();
  const perms = getStoredPermissions();
  if (!token || !user?.role) return <Navigate to="/portal/login" replace />;

  const role = user.role;
  if (perms.includes("pos.inventory.submit.create") || perms.includes("pos.inventory.submit.read")) {
    return <Navigate to="/portal/pos-inventory" replace />;
  }
  if (ACCOUNTING_PERMS.some((p) => perms.includes(p))) {
    return <Navigate to="/portal/accounting" replace />;
  }
  if (LOGISTICS_PERMS.some((p) => perms.includes(p))) {
    return <Navigate to="/portal/shipments" replace />;
  }
  if (METERED_PERMS.some((p) => perms.includes(p))) {
    return <Navigate to="/portal/metered" replace />;
  }
  if (ANALYTICS_PERMS.some((p) => perms.includes(p))) {
    return <Navigate to="/portal/forecast" replace />;
  }
  if (COMPLIANCE_PERMS.some((p) => perms.includes(p))) {
    return <Navigate to="/portal/compliance" replace />;
  }

  if (role === "manufacturer_admin") return <Navigate to="/portal/dashboard" replace />;
  if (perms.includes("pos.sale.create") || perms.includes("pos.shift.open")) return <Navigate to="/portal/pos" replace />;
  if (perms.includes("purchasing.po.read")) return <Navigate to="/portal/purchasing" replace />;
  if (perms.includes("inventory.stock.levels.read")) return <Navigate to="/portal/inventory" replace />;
  if (role === "org_admin") return <Navigate to="/portal/ops" replace />;
  return <Navigate to="/portal/login" replace />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/apply" element={<PrefixApplyPage />} />
        <Route path="/docs" element={<DocsPage />} />
        {/* Manufacturer portal */}
        <Route path="/portal/login" element={<ManufacturerLoginPage />} />
        <Route path="/portal" element={<Navigate to="/portal/home" replace />} />
        <Route path="/portal/home" element={<PortalHomeRedirect />} />
        <Route
          path="/portal/dashboard"
          element={
            <RequirePortalAccess allowedRoles={["manufacturer_admin"]}>
              <ManufacturerDashboardPage />
            </RequirePortalAccess>
          }
        />
        <Route
          path="/portal/products"
          element={
            <RequirePortalAccess allowedRoles={["manufacturer_admin"]}>
              <ManufacturerProductsPage />
            </RequirePortalAccess>
          }
        />
        <Route
          path="/portal/batches"
          element={
            <RequirePortalAccess allowedRoles={["manufacturer_admin"]}>
              <ManufacturerBatchesPage />
            </RequirePortalAccess>
          }
        />
        <Route
          path="/portal/codes"
          element={
            <RequirePortalAccess allowedRoles={["manufacturer_admin"]}>
              <ManufacturerCodesPage />
            </RequirePortalAccess>
          }
        />

        {/* Business roles */}
        <Route
          path="/portal/pos"
          element={
            <RequirePortalAccess
              anyPermissions={[
                "pos.shift.open",
                "pos.sale.create",
                "pos.stock.check",
                "pos.shift.read",
                "pos.terminal.read",
              ]}
            >
              <PosTerminalPage />
            </RequirePortalAccess>
          }
        />

        <Route
          path="/portal/pos-inventory"
          element={
            <RequirePortalAccess
              anyPermissions={["pos.inventory.submit.create", "pos.inventory.submit.read"]}
            >
              <PosInventory />
            </RequirePortalAccess>
          }
        />
        <Route
          path="/portal/metered"
          element={
            <RequirePortalAccess anyPermissions={METERED_PERMS}>
              <PortalMeteredPage />
            </RequirePortalAccess>
          }
        />

        <Route
          path="/portal/forecast"
          element={
            <RequirePortalAccess anyPermissions={ANALYTICS_PERMS}>
              <PortalForecastPage />
            </RequirePortalAccess>
          }
        />

        <Route
          path="/portal/compliance"
          element={
            <RequirePortalAccess anyPermissions={COMPLIANCE_PERMS}>
              <PortalComplianceDashboardPage />
            </RequirePortalAccess>
          }
        />
        <Route
          path="/portal/accounting"
          element={
            <RequirePortalAccess
              anyPermissions={ACCOUNTING_PERMS}
            >
              <PortalAccountingPage />
            </RequirePortalAccess>
          }
        />

        <Route
          path="/portal/shipments"
          element={
            <RequirePortalAccess anyPermissions={LOGISTICS_PERMS}>
              <PortalShipmentsPage />
            </RequirePortalAccess>
          }
        />

        <Route
          path="/portal/shipments/:id"
          element={
            <RequirePortalAccess anyPermissions={LOGISTICS_PERMS}>
              <PortalShipmentDetailPage />
            </RequirePortalAccess>
          }
        />

        <Route
          path="/portal/purchasing"
          element={
            <RequirePortalAccess anyPermissions={["purchasing.po.read", "purchasing.gr.read", "purchasing.suppliers.read"]}>
              <PurchaserDashboardPage />
            </RequirePortalAccess>
          }
        />
        <Route
          path="/portal/inventory"
          element={
            <RequirePortalAccess anyPermissions={["inventory.stock.levels.read", "inventory.stock.movements.read"]}>
              <InventoryDashboardPage />
            </RequirePortalAccess>
          }
        />
        <Route
          path="/portal/ops"
          element={
            <RequirePortalAccess allowedRoles={["org_admin"]}>
              <OpsDashboardPage />
            </RequirePortalAccess>
          }
        />

      </Routes>
    </Router>
  );
}

export default App;
